import PropTypes from 'prop-types';
import React from 'react';
import { connect as connectR } from 'react-redux';
import { Button, ButtonGroup, FormControl } from 'react-bootstrap';
import _ from 'lodash';
import LargeCard from '../main/large-card';
import translate from '../translate';
import ValidatorFormGroup from '../main/validator-form-group';
import { WEBSITE } from '../../constants';
import { getCountryName } from '../../util/country-display';
import { MaybeLoadCountries } from '../../store/countriesDetailsSlice';
import { RegisterPartnerManager } from '../../store/registrationSlice';
import { setSettingsLanguage } from '../../store/genericSettingsSlice';
import { FullResetAction } from '../../store/authSlice';
import { goToActivate } from '../../nav/navigationService';
import { validateEmail } from '../../util/validators';
import { PbRequest } from '../../store/pbRequest';

const DEFAULT_LANG = 'ger';

const VALIDATORS = {
    first_name: {
        isValid(val) {
            return !!val && /^\s*[^\s]{2,}.*$/.test(val);
        },
    },
    last_name: {
        isValid(val) {
            return !!val && /^\s*[^\s]{2,}.*$/.test(val);
        },
    },
    company_name: {
        isValid(val) {
            return !!val && /^\s*[^\s]{2,}.*$/.test(val);
        },
    },
    country_id: {
        isValid(country_id) {
            return !!country_id;
        },
    },
    phone: {
        isValid(val) {
            return !!val && /^\s*\+?\s*(\d[\s\/-]*){5,}$/.test(val);
        },
    },
    email: {
        isValid(val) {
            return !!val && validateEmail(val);
        },
    },
    email_confirm: {
        isValid(val, origEmail) {
            return !!val && val === origEmail;
        },
    },
    password: {
        isValid(val) {
            return (
                !!val &&
                /^(?=\D*\d)(?=.*?[a-zA-Z]).*[\W_].*$/.test(val) && // NOSONAR(javascript:S5852)
                val.length > 7
            );
        },
    },
    password_confirm: {
        isValid(val, origPass) {
            return !!val && val === origPass;
        },
    },
    terms_accepted: {
        isValid(val) {
            return !!val;
        },
    },
};

class RegistrationBasicForm extends React.Component {
    static propTypes = {
        strings: PropTypes.object.isRequired,
        MaybeLoadCountries: PropTypes.func.isRequired,
        SetLanguage: PropTypes.func.isRequired,
        RegisterPartnerManager: PropTypes.func.isRequired,
        UpdatePartnerEmail: PropTypes.func.isRequired,
        countries: PropTypes.shape({}).isRequired,
        registrationRequest: PropTypes.shape({
            status: PropTypes.string,
            errors: PropTypes.arrayOf(PropTypes.string),
        }).isRequired,
        language: PropTypes.string.isRequired,
        setSettingsLanguage: PropTypes.func.isRequired,
        FullResetAction: PropTypes.func.isRequired,
    };

    state = {
        data: {
            salutation: 'M',
            first_name: '',
            last_name: '',
            company_name: '',
            phone: '',
            email: '',
            email_confirm: '',
            terms_accepted: false,
            password: '',
            password_confirm: '',
            country_id: 1,
            lang: DEFAULT_LANG,
        },
        dirty: {},
        isValid: false,
    };

    componentDidMount() {
        this.props.FullResetAction();
        this.props.MaybeLoadCountries();
        this.props.setSettingsLanguage(DEFAULT_LANG);
    }

    getErrorMessage = () => {
        let errorMessage = null;
        const occurredError = this._resolveFirstTagOrUndefined(
            this.props.registrationRequest.errors
        );
        if (occurredError) {
            let errorString;
            if (occurredError === 'USER_ALREADY_CREATED') {
                errorString = this.props.strings.duplicateUserAlert;
            } else if (occurredError === 'EMAIL_INVALID') {
                errorString = this.props.strings.errorEmail;
            } else {
                errorString = this.props.strings.generalError;
            }

            errorMessage = (
                <div className="alert alert-danger pull-left">
                    {`${errorString}`}
                </div>
            );
        }
        return errorMessage;
    };

    _getAgbUrl = () => {
        return `${WEBSITE}${this.props.strings.agbUrlRoute}`;
    };

    _getPPUrl = () => {
        return `${WEBSITE}${this.props.strings.ppUrlRoute}`;
    };

    _handleSubmit = (event) => {
        event.preventDefault();
        if (this.state.isValid) {
            const userCreationData = this.state.data;
            this.props.RegisterPartnerManager({
                data: userCreationData,
                onSuccess: () => {
                    goToActivate();
                },
            });
        }
    };

    _resolveFirstTagOrUndefined = (errors) => {
        return errors && errors[0] ? errors[0] : undefined;
    };

    _markDirty = (key) => {
        const newDirt = {};
        newDirt[key] = true;
        const newState = _.extend({}, this.state, {
            dirty: _.extend({}, this.state.dirty, newDirt),
        });
        this.setState(newState);
    };

    _isDataValid = (data) => {
        return Object.keys(VALIDATORS).reduce((sum, fieldName) => {
            switch (fieldName) {
                case 'password_confirm':
                    sum =
                        sum &&
                        VALIDATORS.password_confirm.isValid(
                            data.password_confirm,
                            data.password
                        );
                    break;
                case 'email_confirm':
                    sum =
                        sum &&
                        VALIDATORS.email_confirm.isValid(
                            data.email_confirm,
                            data.email
                        );
                    break;
                default:
                    sum = sum && VALIDATORS[fieldName].isValid(data[fieldName]);
            }
            return sum;
        }, true);
    };

    _persist = (field, value) => {
        this._markDirty(field);
        const data = _.extend({}, this.state.data);
        data[field] = value;
        const isValid = this._isDataValid(data);
        this.setState({ data, isValid });
        if (field === 'lang') {
            this.props.setSettingsLanguage(value);
        }
    };

    render() {
        const selectedLanguage = this.state.data.lang;

        const errorMessage = this.getErrorMessage();
        const agbUrl = this._getAgbUrl();
        const ppUrl = this._getPPUrl();

        return (
            <LargeCard
                type="card-edit"
                title={this.props.strings.title}
                description={
                    <p>
                        {this.props.strings.description}
                        <br />
                        {this.props.strings.description2}
                    </p>
                }
            >
                <form onSubmit={this._handleSubmit} method="POST" role="form">
                    <ValidatorFormGroup label={this.props.strings.language}>
                        <ButtonGroup className="radio-tabs">
                            <Button
                                className={`col-xs-6 btn btn-primary${selectedLanguage === 'ger' ? ' active' : ''}`}
                                onClick={() => this._persist('lang', 'ger')}
                            >
                                {this.props.strings.languageGer}
                            </Button>
                            <Button
                                className={`col-xs-6 btn btn-primary${selectedLanguage === 'eng' ? ' active' : ''}`}
                                onClick={() => this._persist('lang', 'eng')}
                            >
                                {this.props.strings.languageEng}
                            </Button>
                        </ButtonGroup>
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label={this.props.strings.company_name}
                        hideErrorLabel={
                            !this.state.dirty.company_name ||
                            VALIDATORS.company_name.isValid(
                                this.state.data.company_name
                            )
                        }
                        errorLabel={this.props.strings.company_nameError}
                    >
                        <FormControl
                            type="text"
                            className={`input-text-color`}
                            value={this.state.data.company_name}
                            name="company_name"
                            onChange={(event) => {
                                this._persist(
                                    'company_name',
                                    event.target.value
                                );
                            }}
                            required
                        />
                    </ValidatorFormGroup>
                    <ValidatorFormGroup label={this.props.strings.salutation}>
                        <ButtonGroup className="radio-tabs">
                            <Button
                                className={`col-xs-6 btn btn-primary${this.state.data.salutation === 'M' ? ' active' : ''}`}
                                onClick={() => this._persist('salutation', 'M')}
                            >
                                {this.props.strings.salutationMr}
                            </Button>
                            <Button
                                className={`col-xs-6 btn btn-primary${this.state.data.salutation === 'F' ? ' active' : ''}`}
                                onClick={() => this._persist('salutation', 'F')}
                            >
                                {this.props.strings.salutationMrs}
                            </Button>
                        </ButtonGroup>
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label={this.props.strings.first_name}
                        hideErrorLabel={
                            !this.state.dirty.first_name ||
                            VALIDATORS.first_name.isValid(
                                this.state.data.first_name
                            )
                        }
                        errorLabel={this.props.strings.first_nameError}
                    >
                        <FormControl
                            type="text"
                            className={`input-text-color`}
                            value={this.state.data.first_name}
                            name="first_name"
                            onChange={(event) => {
                                this._persist('first_name', event.target.value);
                            }}
                            required
                        />
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label={this.props.strings.last_name}
                        hideErrorLabel={
                            !this.state.dirty.last_name ||
                            VALIDATORS.last_name.isValid(
                                this.state.data.last_name
                            )
                        }
                        errorLabel={this.props.strings.last_nameError}
                    >
                        <FormControl
                            type="text"
                            className={`input-text-color`}
                            value={this.state.data.last_name}
                            name="last_name"
                            onChange={(event) => {
                                this._persist('last_name', event.target.value);
                            }}
                            required
                        />
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label={this.props.strings.country_id}
                        hideErrorLabel={
                            !(
                                this.state.dirty.country_id ||
                                this.props.forceValidation
                            ) ||
                            VALIDATORS.country_id.isValid(
                                this.state.data.country_id
                            )
                        }
                        errorLabel={this.props.strings.country_idError}
                    >
                        <select
                            className="form-control"
                            name="country_id"
                            onChange={(event) => {
                                this._persist('country_id', event.target.value);
                            }}
                            value={this.state.country_id}
                            required
                        >
                            {Object.values(this.props.countries).map(
                                (country) => (
                                    <option key={country.id} value={country.id}>
                                        {getCountryName(
                                            country,
                                            selectedLanguage
                                        )}
                                    </option>
                                )
                            )}
                        </select>
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label={this.props.strings.phone}
                        hideErrorLabel={
                            !this.state.dirty.phone ||
                            VALIDATORS.phone.isValid(this.state.data.phone)
                        }
                        errorLabel={this.props.strings.phoneError}
                    >
                        <FormControl
                            type="text"
                            className={`input-text-color`}
                            value={this.state.data.phone}
                            name="phone"
                            onChange={(event) => {
                                this._persist('phone', event.target.value);
                            }}
                            required
                        />
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label={this.props.strings.email}
                        hideErrorLabel={
                            !this.state.dirty.email ||
                            VALIDATORS.email.isValid(this.state.data.email)
                        }
                        errorLabel={this.props.strings.emailError}
                    >
                        <FormControl
                            type="text"
                            className={`input-text-color`}
                            value={this.state.data.email}
                            name="email"
                            onChange={(event) => {
                                this._persist('email', event.target.value);
                            }}
                            required
                        />
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label={this.props.strings.email_confirm}
                        hideErrorLabel={
                            !this.state.dirty.email_confirm ||
                            VALIDATORS.email_confirm.isValid(
                                this.state.data.email_confirm,
                                this.state.data.email
                            )
                        }
                        helpLabel={this.props.strings.email_confirmHelp}
                        errorLabel={this.props.strings.email_confirmError}
                    >
                        <FormControl
                            type="text"
                            className={`input-text-color`}
                            value={this.state.data.email_confirm}
                            name="email_confirm"
                            onChange={(event) => {
                                this._persist(
                                    'email_confirm',
                                    event.target.value
                                );
                            }}
                            required
                        />
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label={this.props.strings.password}
                        hideErrorLabel={
                            !this.state.dirty.password ||
                            VALIDATORS.password.isValid(
                                this.state.data.password
                            )
                        }
                        helpLabel={this.props.strings.passwordHelp}
                        errorLabel={this.props.strings.passwordError}
                    >
                        <FormControl
                            type="password"
                            className={`input-text-color`}
                            value={this.state.data.password}
                            name="password"
                            onChange={(event) => {
                                this._persist('password', event.target.value);
                            }}
                            required
                        />
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label={this.props.strings.password_confirm}
                        hideErrorLabel={
                            !this.state.dirty.password_confirm ||
                            VALIDATORS.password_confirm.isValid(
                                this.state.data.password_confirm,
                                this.state.data.password
                            )
                        }
                        errorLabel={this.props.strings.password_confirmError}
                    >
                        <FormControl
                            type="password"
                            className={`input-text-color`}
                            value={this.state.data.password_confirm}
                            name="password_confirm"
                            onChange={(event) => {
                                this._persist(
                                    'password_confirm',
                                    event.target.value
                                );
                            }}
                            required
                        />
                    </ValidatorFormGroup>
                    <ValidatorFormGroup
                        label=""
                        hideErrorLabel={
                            !this.state.dirty.terms_accepted ||
                            VALIDATORS.terms_accepted.isValid(
                                this.state.data.terms_accepted
                            )
                        }
                        errorLabel={this.props.strings.accept_agbError}
                    >
                        <div className="checkbox checkbox-primary checkbox-custom">
                            <input
                                type="checkbox"
                                id="terms_accepted"
                                className="styled"
                                checked={this.state.terms_accepted}
                                onChange={(event) => {
                                    this._persist(
                                        'terms_accepted',
                                        event.target.checked
                                    );
                                }}
                            />
                            <label htmlFor="terms_accepted">
                                {this.props.strings.termsAndCond1}
                                <a
                                    className="icon"
                                    href={agbUrl}
                                    target="_blank"
                                >
                                    {this.props.strings.aGB}
                                </a>
                                {this.props.strings.and}
                                <a
                                    className="icon"
                                    href={ppUrl}
                                    target="_blank"
                                >
                                    {this.props.strings.pp}
                                </a>
                                {this.props.strings.termsAndCond2}
                            </label>
                        </div>
                    </ValidatorFormGroup>
                    <div className="clearfix" />
                    <div className="form-footer">
                        {errorMessage}
                        <Button
                            type="submit"
                            disabled={!this.state.isValid}
                            className="btn btn-primary pull-right"
                        >
                            {this.props.strings.registerLabel}
                        </Button>
                    </div>
                </form>
            </LargeCard>
        );
    }
}

export default connectR(
    (store) => ({
        countries: store.countriesDetails.idToCountry || {},
        registrationRequest: store.pbRequest[PbRequest.REGISTRATION_REQUEST],
    }),
    {
        MaybeLoadCountries,
        RegisterPartnerManager,
        setSettingsLanguage,
        FullResetAction,
    }
)(translate('RegistrationBasicForm')(RegistrationBasicForm));
